/**
 * WebAuthn Cryptographic Buffer Conversion Utilities
 * Handles W3C WebAuthn Level 2/3 Base64URL encoding/decoding
 * for navigator.credentials.create() and navigator.credentials.get().
 */

/**
 * Converts Base64URL or Base64 string to Uint8Array/ArrayBuffer
 */
export function base64UrlToBuffer(base64Url) {
  if (!base64Url) return new Uint8Array(0).buffer;
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Converts an ArrayBuffer to a Base64URL string (URL-safe, no padding)
 */
export function bufferToBase64Url(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Prepares server-generated CredentialCreateOptions for navigator.credentials.create()
 */
export function prepareCredentialCreationOptions(serverOptions) {
  const options = { ...serverOptions };

  // Challenge must be ArrayBuffer
  if (typeof options.challenge === 'string') {
    options.challenge = base64UrlToBuffer(options.challenge);
  }

  // User ID must be ArrayBuffer
  if (options.user && typeof options.user.id === 'string') {
    options.user.id = base64UrlToBuffer(options.user.id);
  }

  // Exclude credentials IDs must be ArrayBuffer
  if (Array.isArray(options.excludeCredentials)) {
    options.excludeCredentials = options.excludeCredentials.map((cred) => ({
      ...cred,
      id: typeof cred.id === 'string' ? base64UrlToBuffer(cred.id) : cred.id,
    }));
  }

  return options;
}

/**
 * Serializes PublicKeyCredential from navigator.credentials.create() into JSON for server verification
 */
export function serializeAttestationResponse(credential) {
  const response = credential.response;
  return {
    id: credential.id,
    rawId: bufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64Url(response.attestationObject),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
    },
    clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {},
  };
}

/**
 * Prepares server-generated AssertionOptions for navigator.credentials.get()
 */
export function prepareAssertionOptions(serverOptions) {
  const options = { ...serverOptions };

  // Challenge must be ArrayBuffer
  if (typeof options.challenge === 'string') {
    options.challenge = base64UrlToBuffer(options.challenge);
  }

  // Allowed credentials IDs must be ArrayBuffer
  if (Array.isArray(options.allowCredentials)) {
    options.allowCredentials = options.allowCredentials.map((cred) => ({
      ...cred,
      id: typeof cred.id === 'string' ? base64UrlToBuffer(cred.id) : cred.id,
    }));
  }

  return options;
}

/**
 * Serializes PublicKeyCredential from navigator.credentials.get() into JSON for server verification
 */
export function serializeAssertionResponse(assertion) {
  const response = assertion.response;
  return {
    id: assertion.id,
    rawId: bufferToBase64Url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64Url(response.authenticatorData),
      clientDataJSON: bufferToBase64Url(response.clientDataJSON),
      signature: bufferToBase64Url(response.signature),
      userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
    },
    clientExtensionResults: assertion.getClientExtensionResults ? assertion.getClientExtensionResults() : {},
  };
}
